package com.elan.edubridge.ui.staff

import com.elan.edubridge.shared.di.SharedGraph
import com.elan.edubridge.shared.domain.model.ClassData
import com.elan.edubridge.shared.domain.model.ClassSchedule
import com.elan.edubridge.shared.domain.model.Permission
import com.elan.edubridge.shared.domain.model.SessionState
import com.elan.edubridge.shared.domain.model.UpdateClassRequest
import com.elan.edubridge.ui.shell.escapeHtml
import com.elan.edubridge.ui.shell.icon
import kotlinx.browser.document
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLFormElement
import org.w3c.dom.HTMLInputElement

/** 학생 관리에서 여는 기존 반의 수업 정보 편집 모달. */
object ClassEditModal {
    private val scope = MainScope()
    private val repository = SharedGraph.createRepository()
    private val commandRepository = SharedGraph.createAcademyCommandRepository()

    fun open(session: SessionState, academyId: String) {
        if (session.activeMembership?.academyId != academyId || !session.activePermission.atLeast(Permission.EDITOR)) {
            Toast.error("반 정보 수정은 편집자 이상 권한이 필요해요.")
            return
        }
        scope.launch {
            val result = repository.classesResult(academyId)
            // 반 목록을 못 읽었는데 빈 모달을 열면 스태프가 이미 있는 반을 다시 만든다(#30).
            if (!result.isSuccess && result.items.isEmpty()) {
                Toast.error("반 목록을 불러오지 못했어요. 잠시 후 다시 시도해 주세요.")
                return@launch
            }
            val classes = result.items
                .sortedWith(compareBy({ it.schedule.daysOfWeek.minOrNull() ?: 8 }, { it.schedule.startMinuteOfDay }, { it.className }))
            openList(session, academyId, classes)
        }
    }

    private fun openList(session: SessionState, academyId: String, classes: List<ClassData>) {
        val backdrop = mountModal(
            "class-manager-title",
            """
            <header class="eb-modal__header">
                <h2 id="class-manager-title">반 관리</h2>
                <button class="eb-icon-button" id="class-manager-close" type="button" aria-label="닫기">${icon("icon-close", "md")}</button>
            </header>
            <div class="eb-modal__body">
                ${classListHtml(classes)}
            </div>
            <footer class="eb-modal__footer">
                <button class="eb-button eb-button--ghost" id="class-manager-done" type="button">닫기</button>
            </footer>
            """.trimIndent(),
        )
        fun close() = backdrop.remove()
        (backdrop.querySelector("#class-manager-close") as? HTMLElement)?.onclick = { close(); null }
        (backdrop.querySelector("#class-manager-done") as? HTMLElement)?.onclick = { close(); null }
        backdrop.onclick = { event -> if (event.target == backdrop) close(); null }
        val editButtons = backdrop.querySelectorAll(".edit-class")
        for (index in 0 until editButtons.length) {
            val button = editButtons.item(index) as? HTMLElement ?: continue
            button.onclick = {
                classes.getOrNull(button.getAttribute("data-class-index")?.toIntOrNull() ?: -1)?.let { classData ->
                    backdrop.remove()
                    openEditor(session, academyId, classData)
                }
                null
            }
        }
    }

    private fun classListHtml(classes: List<ClassData>): String {
        if (classes.isEmpty()) {
            return """
                <div class="eb-empty-state">
                    ${icon("icon-calendar", "xl")}
                    <h3>등록된 반이 없어요</h3>
                    <p>등록된 반의 이름·과목·수업 시간을 여기서 수정할 수 있어요.</p>
                </div>
            """.trimIndent()
        }
        return """
            <div class="eb-table-scroll">
                <table class="eb-table">
                    <thead><tr><th scope="col">반</th><th scope="col">수업 시간</th><th scope="col"><span class="eb-sr-only">편집</span></th></tr></thead>
                    <tbody>
                        ${classes.mapIndexed { index, classData ->
                            """
                            <tr>
                                <td><strong>${classData.className.escapeHtml()}</strong><br><span class="eb-meta">${classData.subject.escapeHtml()}</span></td>
                                <td>${classData.schedule.displayLabel.escapeHtml()}</td>
                                <td><button class="eb-button eb-button--ghost eb-button--sm edit-class" data-class-index="$index" type="button">편집</button></td>
                            </tr>
                            """.trimIndent()
                        }.joinToString("")}
                    </tbody>
                </table>
            </div>
        """.trimIndent()
    }

    private fun openEditor(session: SessionState, academyId: String, classData: ClassData) {
        val backdrop = mountModal(
            "class-editor-title",
            """
            <header class="eb-modal__header">
                <h2 id="class-editor-title">반 정보 수정</h2>
                <button class="eb-icon-button" id="class-editor-close" type="button" aria-label="닫기">${icon("icon-close", "md")}</button>
            </header>
            <form class="eb-modal__body" id="class-editor-form">
                <div class="eb-field"><label for="class-editor-name">반 이름</label><input id="class-editor-name" maxlength="60" value="${classData.className.escapeHtml()}" required></div>
                <div class="eb-field"><label for="class-editor-subject">과목 <span class="eb-meta">(선택)</span></label><input id="class-editor-subject" maxlength="60" value="${classData.subject.escapeHtml()}"></div>
                <fieldset class="eb-fieldset">
                    <legend>수업 요일</legend>
                    <div>
                        ${DAYS.joinToString(" ") { (value, label) ->
                            val checked = if (value in classData.schedule.daysOfWeek) " checked" else ""
                            """<label><input class="class-editor-day" type="checkbox" value="$value"$checked> $label</label>"""
                        }}
                    </div>
                </fieldset>
                <div class="eb-field"><label for="class-editor-start">시작 시간</label><input id="class-editor-start" type="time" value="${formatTime(classData.schedule.startMinuteOfDay)}" required></div>
                <div class="eb-field">
                    <label for="class-editor-end">종료 시간</label>
                    <input id="class-editor-end" type="time" value="${formatEndTime(classData.schedule.endMinuteOfDay)}" required>
                    <label><input id="class-editor-end-midnight" type="checkbox"${if (classData.schedule.endMinuteOfDay == MINUTES_PER_DAY) " checked" else ""}> 24:00(자정)에 종료</label>
                </div>
                <p class="eb-form-error" id="class-editor-error" role="alert" hidden></p>
            </form>
            <footer class="eb-modal__footer">
                <button class="eb-button eb-button--ghost" id="class-editor-cancel" type="button">취소</button>
                <button class="eb-button eb-button--primary" id="class-editor-save" type="submit" form="class-editor-form">저장</button>
            </footer>
            """.trimIndent(),
        )
        var submitting = false
        fun close() {
            if (!submitting) backdrop.remove()
        }
        fun setSubmitting(value: Boolean) {
            submitting = value
            listOf("#class-editor-close", "#class-editor-cancel", "#class-editor-save").forEach { selector ->
                (backdrop.querySelector(selector) as? HTMLButtonElement)?.disabled = value
            }
        }
        (backdrop.querySelector("#class-editor-close") as? HTMLElement)?.onclick = { close(); null }
        (backdrop.querySelector("#class-editor-cancel") as? HTMLElement)?.onclick = { close(); null }
        backdrop.onclick = { event -> if (event.target == backdrop) close(); null }
        val midnight = backdrop.querySelector("#class-editor-end-midnight") as? HTMLInputElement
        val endTime = backdrop.querySelector("#class-editor-end") as? HTMLInputElement
        fun syncEndTimeInput() {
            endTime?.disabled = midnight?.checked == true
        }
        midnight?.onchange = { syncEndTimeInput(); null }
        syncEndTimeInput()
        (backdrop.querySelector("#class-editor-form") as? HTMLFormElement)?.onsubmit = { event ->
            event.preventDefault()
            if (!submitting) submitEditor(session, academyId, classData, backdrop, ::close, ::setSubmitting)
            null
        }
        (backdrop.querySelector("#class-editor-name") as? HTMLInputElement)?.focus()
    }

    private fun submitEditor(
        session: SessionState,
        academyId: String,
        classData: ClassData,
        backdrop: HTMLElement,
        close: () -> Unit,
        setSubmitting: (Boolean) -> Unit,
    ) {
        val name = (backdrop.querySelector("#class-editor-name") as? HTMLInputElement)?.value?.trim().orEmpty()
        val subject = (backdrop.querySelector("#class-editor-subject") as? HTMLInputElement)?.value?.trim().orEmpty()
        val days = mutableListOf<Int>()
        val dayInputs = backdrop.querySelectorAll(".class-editor-day")
        for (index in 0 until dayInputs.length) {
            val input = dayInputs.item(index) as? HTMLInputElement ?: continue
            if (input.checked) input.value.toIntOrNull()?.let(days::add)
        }
        val start = (backdrop.querySelector("#class-editor-start") as? HTMLInputElement)?.value?.let(::parseTime)
        val endsAtMidnight = (backdrop.querySelector("#class-editor-end-midnight") as? HTMLInputElement)?.checked == true
        val end = if (endsAtMidnight) MINUTES_PER_DAY else {
            (backdrop.querySelector("#class-editor-end") as? HTMLInputElement)?.value?.let(::parseTime)
        }
        val error = backdrop.querySelector("#class-editor-error") as? HTMLElement ?: return
        fun showError(message: String) {
            error.textContent = message
            error.removeAttribute("hidden")
        }
        when {
            name.isEmpty() -> showError("반 이름을 입력해 주세요.")
            name.length > 60 || subject.length > 60 -> showError("반 이름과 과목은 60자까지 입력할 수 있어요.")
            days.isEmpty() -> showError("수업 요일을 하나 이상 선택해 주세요.")
            start == null || end == null -> showError("시작·종료 시간을 모두 입력해 주세요.")
            end <= start -> showError("종료 시간은 시작 시간보다 늦어야 해요.")
            else -> {
                error.setAttribute("hidden", "")
                setSubmitting(true)
                scope.launch {
                    try {
                        val result = commandRepository.updateClass(
                            UpdateClassRequest(
                                academyId = academyId,
                                classId = classData.id,
                                className = name,
                                subject = subject,
                                schedule = ClassSchedule(days.sorted(), start, end),
                            ),
                        )
                        if (result.isSuccess) {
                            backdrop.remove()
                            Toast.success("반 정보를 저장했어요.")
                            open(session, academyId)
                        } else {
                            showError(result.errorMessage ?: "저장에 실패했어요. 다시 시도해 주세요.")
                        }
                    } finally {
                        setSubmitting(false)
                    }
                }
            }
        }
    }

    private fun mountModal(labelledBy: String, content: String): HTMLElement {
        val backdrop = document.createElement("div") as HTMLElement
        backdrop.className = "eb-modal-backdrop"
        backdrop.innerHTML = """
            <section class="eb-modal" role="dialog" aria-modal="true" aria-labelledby="$labelledBy">$content</section>
        """.trimIndent()
        document.body?.appendChild(backdrop)
        return backdrop
    }

    private fun parseTime(value: String): Int? {
        val parts = value.split(":")
        if (parts.size != 2) return null
        val hour = parts[0].toIntOrNull() ?: return null
        val minute = parts[1].toIntOrNull() ?: return null
        return if (hour in 0..23 && minute in 0..59) hour * 60 + minute else null
    }

    private fun formatTime(minuteOfDay: Int): String =
        "${(minuteOfDay / 60).coerceIn(0, 23).toString().padStart(2, '0')}:${(minuteOfDay % 60).coerceIn(0, 59).toString().padStart(2, '0')}"

    /** HTML time input cannot carry 24:00, so the checked sentinel above owns that value. */
    private fun formatEndTime(minuteOfDay: Int): String =
        formatTime(if (minuteOfDay == MINUTES_PER_DAY) MINUTES_PER_DAY - 1 else minuteOfDay)

    private val DAYS = listOf(1 to "월", 2 to "화", 3 to "수", 4 to "목", 5 to "금", 6 to "토", 7 to "일")
    private const val MINUTES_PER_DAY = 24 * 60
}
